import Microgame from '../engine/Microgame.js';

export default class BasketGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Basket !`, en: `Basketball!`, es: `¡Baloncesto!` },
            controls: { fr: `Glisse vers le panier pour lancer la balle`, en: `Swipe towards the hoop to throw`, es: `Desliza hacia la canasta para lanzar` },
            objective: { fr: `Marque un panier !`, en: `Score a basket!`, es: `¡Anota una canasta!` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`,
            actions: [
                { type: 'swipe', fromX: 20, fromY: 70, toX: 62, toY: 35, duration: 300 },
                { type: 'wait', duration: 460 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.ball = null;
        this.hoop = null;
        this.backboard = null;
        this.trajectory = null;
        this.state = 'idle'; // idle, dragging, flying
        this.ballPos = { x: 20, y: 70 }; // % (Top/Left)
        this.prevBallPos = { x: 20, y: 70 };
        this.velocity = { x: 0, y: 0 };
        this.dragStart = { x: 0, y: 0 };
        this.gravity = 150; // Gravity strength

        // Difficulty scaling
        const speedMult = engine.speedMultiplier || 1.0;

        // Progression is much smoother: at max speed (x4.0), it corresponds to the older speed of x2.0
        const adjustedSpeed = 1 + (speedMult - 1) * (1 / 3);

        // Engine does: timer = duration / speedMult. So we compensate.
        this.duration = 3000 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "DRAG & SHOOT!";

        // Backboard
        this.backboard = document.createElement('div');
        this.backboard.style.position = 'absolute';
        this.backboard.style.left = '88%';
        this.backboard.style.top = '35%';
        this.backboard.style.width = '5px';
        this.backboard.style.height = '80px';
        this.backboard.style.background = this.secondaryColor;
        this.backboard.style.transform = 'translate(-50%, -50%)';
        this.backboard.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        this.container.appendChild(this.backboard);

        // Hoop (Right side, in front of backboard)
        this.hoop = document.createElement('div');
        this.hoop.style.position = 'absolute';
        this.hoop.style.left = '80%';
        this.hoop.style.top = '40%';
        this.hoop.style.width = '60px';
        this.hoop.style.height = '5px';
        this.hoop.style.background = this.secondaryColor;
        this.hoop.style.borderRadius = '5px';
        this.hoop.style.transform = 'translateX(-50%)';
        this.hoop.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        this.container.appendChild(this.hoop);

        // Net
        const net = document.createElement('div');
        net.style.position = 'absolute';
        net.style.top = '5px';
        net.style.left = '5px';
        net.style.width = '50px';
        net.style.height = '40px';
        net.style.border = `2px solid ${this.secondaryColor}`;
        net.style.borderTop = 'none';
        net.style.opacity = '0.5';
        net.style.borderRadius = '0 0 10px 10px';
        this.hoop.appendChild(net);

        // Ball
        this.ball = document.createElement('div');
        this.ball.style.position = 'absolute';
        this.ball.style.width = '30px';
        this.ball.style.height = '30px';
        this.ball.style.background = this.primaryColor;
        this.ball.style.borderRadius = '50%';
        this.ball.style.left = `${this.ballPos.x}%`;
        this.ball.style.top = `${this.ballPos.y}%`;
        this.ball.style.transform = 'translate(-50%, -50%)';
        this.ball.style.boxShadow = `0 0 10px ${this.primaryColor}`;
        this.ball.style.cursor = 'pointer';
        this.ball.style.touchAction = 'none';
        this.container.appendChild(this.ball);

        // Trajectory Line (Arrow)
        this.trajectory = document.createElement('div');
        this.trajectory.style.position = 'absolute';
        this.trajectory.style.height = '4px';
        this.trajectory.style.background = this.secondaryColor;
        this.trajectory.style.transformOrigin = 'left center';
        this.trajectory.style.display = 'none';
        this.trajectory.style.pointerEvents = 'none';
        this.trajectory.style.borderRadius = '2px';
        this.container.appendChild(this.trajectory);

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.container.addEventListener('pointerdown', this.onDown);
        this.container.addEventListener('pointermove', this.onMove);
        this.container.addEventListener('pointerup', this.onUp);
        this.container.addEventListener('pointercancel', this.onUp);
    }

    onDown(e) {
        if (!this.active || this.won || this.state !== 'idle') return;

        const rect = this.ball.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const dist = Math.hypot(e.clientX - centerX, e.clientY - centerY);

        if (dist < 60) {
            this.state = 'dragging';
            this.dragStart = { x: centerX, y: centerY };
            this.trajectory.style.display = 'block';
            this.updateTrajectory(e.clientX, e.clientY);
        }
    }

    onMove(e) {
        if (this.state !== 'dragging') return;
        this.updateTrajectory(e.clientX, e.clientY);
    }

    updateTrajectory(mouseX, mouseY) {
        // Clear old dots
        if (!this.dots) this.dots = [];
        this.dots.forEach(d => d.style.display = 'none');

        const dx = this.dragStart.x - mouseX;
        const dy = this.dragStart.y - mouseY;

        const rect = this.container.getBoundingClientRect();

        // Calculate launch velocity (simulating onUp logic)
        const powerCap = 200; // Cap visual power
        const dragDist = Math.hypot(dx, dy);
        const scale = dragDist > powerCap ? powerCap / dragDist : 1;
        const launchVx = (dx * scale / rect.width) * 300 * 2.5;
        const launchVy = (dy * scale / rect.height) * 300 * 2.5; // Same multiplier as onUp

        // Simulate
        let simX = (this.dragStart.x - rect.left) / rect.width * 100;
        let simY = (this.dragStart.y - rect.top) / rect.height * 100;
        let simVx = launchVx;
        let simVy = launchVy;
        const dt = 0.05;
        const g = this.gravity; // 150

        // Create dots if needed
        while (this.dots.length < 15) {
            const dot = document.createElement('div');
            dot.style.position = 'absolute';
            dot.style.width = '6px';
            dot.style.height = '6px';
            dot.style.background = 'rgba(255, 255, 255, 0.4)';
            dot.style.borderRadius = '50%';
            dot.style.pointerEvents = 'none';
            this.container.appendChild(dot);
            this.dots.push(dot);
        }

        for (let i = 0; i < 15; i++) {
            simVy += g * dt;
            simX += simVx * dt;
            simY += simVy * dt;

            this.dots[i].style.left = `${simX}%`;
            this.dots[i].style.top = `${simY}%`;
            this.dots[i].style.display = 'block';

            // Fade out
            this.dots[i].style.opacity = 1 - (i / 15);
        }
    }

    onUp(e) {
        if (this.state !== 'dragging') return;
        this.state = 'flying';
        // Hide dots
        if (this.dots) this.dots.forEach(d => d.style.display = 'none');
        if (this.trajectory) this.trajectory.style.display = 'none'; // Fallback

        const mouseX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
        const mouseY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;

        const dx = this.dragStart.x - mouseX;
        const dy = this.dragStart.y - mouseY;

        const power = 2.5;
        // Apply same limiting logic if needed, but for now raw drag
        // Wait, updateTrajectory used limited drag. We should probably limit here too for consistency?
        // User didn't complain about power, just vision. Let's keep existing power logic but align visuals.
        // Actually, let's limit power to match visuals to avoid confusion
        const dist = Math.hypot(dx, dy);
        const maxDist = 200;
        const scale = dist > maxDist ? maxDist / dist : 1;

        this.velocity.x = (dx * scale / this.container.clientWidth) * 300 * power;
        this.velocity.y = (dy * scale / this.container.clientHeight) * 300 * power;
    }

    update(dt) {
        if (!this.active || this.won) return;

        if (this.state === 'flying') {
            // Freeze the timer while the ball is mid-air so the user doesn't lose during the animation
            if (this.engine && this.engine.timer !== Infinity) {
                this.engine.timer += dt;
            }

            // Store previous position
            this.prevBallPos = { ...this.ballPos };

            this.velocity.y += this.gravity * (dt / 1000);

            this.ballPos.x += this.velocity.x * (dt / 1000);
            this.ballPos.y += this.velocity.y * (dt / 1000);

            // Backboard Collision
            if (this.ballPos.x > 85 && this.ballPos.x < 90) {
                if (this.ballPos.y > 25 && this.ballPos.y < 45) {
                    if (this.velocity.x > 0) {
                        this.velocity.x *= -0.6;
                        this.ballPos.x = 85;

                        this.backboard.style.transform = 'translate(-50%, -50%) scale(1.1)';
                        setTimeout(() => this.backboard.style.transform = 'translate(-50%, -50%)', 100);
                    }
                }
            }

            this.ball.style.left = `${this.ballPos.x}%`;
            this.ball.style.top = `${this.ballPos.y}%`;

            // Hoop Crossing Logic
            // Hoop Y is 40%. Ball must cross from < 40 to > 40.
            // Hoop X center is 80%. Width 60px (~15%). Range 72.5% to 87.5%
            // Let's use a slightly generous range: 75% to 85%.

            const hoopY = 40;
            const hoopXMin = 75;
            const hoopXMax = 85;

            if (this.velocity.y > 0) { // Moving down
                if (this.prevBallPos.y <= hoopY && this.ballPos.y >= hoopY) {
                    // Crossed the line!
                    // Check X intersection at the exact crossing point
                    // Interpolate X at Y=40
                    const t = (hoopY - this.prevBallPos.y) / (this.ballPos.y - this.prevBallPos.y);
                    const crossX = this.prevBallPos.x + (this.ballPos.x - this.prevBallPos.x) * t;

                    if (crossX >= hoopXMin && crossX <= hoopXMax) {
                        // SCORED!
                        this.score = (this.score || 0) + 1;
                        this.hoop.style.transform = 'translateX(-50%) scale(1.2)';
                        this.hoop.style.background = this.primaryColor;

                        const target = (this.engine.activeChallenge && this.engine.activeChallenge.config && this.engine.activeChallenge.config.targetScore) || 1;

                        if (this.score >= target) {
                            this.win();
                        } else {
                            // Reset for next shot
                            // Don't fully reset yet, let it fall through then reset
                            setTimeout(() => {
                                if (this.active && !this.won) {
                                    this.resetBall();
                                    this.hoop.style.transform = 'translateX(-50%) scale(1)';
                                    this.hoop.style.background = this.secondaryColor;
                                }
                            }, 500);
                        }
                    }
                }
            }

            // Miss or out of bounds
            if ((this.ballPos.y > 110 || this.ballPos.x > 110 || this.ballPos.x < -10) && !this.won) {
                // If we haven't won yet, this is a miss.
                // In streak mode with multiple shots, a miss might be a loss OR just a retry?
                // Usually "Streak" means perfect. But "Score 5 baskets" might allow misses?
                // Let's assume strict "Don't miss" if it's a Challenge, unless specified.
                // But since it's "Lives" based usually, let's just fail this attempt.
                // Actually, if we miss in a multi-score game, we usually lose the microgame.
                this.lose();
            }
        }
    }

    resetBall() {
        this.state = 'idle';
        this.ballPos = { x: 20, y: 70 };
        this.velocity = { x: 0, y: 0 };
        this.trajectory.style.display = 'none';

        this.ball.style.left = `${this.ballPos.x}%`;
        this.ball.style.top = `${this.ballPos.y}%`;
        this.ball.style.transform = 'translate(-50%, -50%)';
    }

    cleanup() {
        super.cleanup();
        this.container.removeEventListener('pointerdown', this.onDown);
        this.container.removeEventListener('pointermove', this.onMove);
        this.container.removeEventListener('pointerup', this.onUp);
        this.container.removeEventListener('pointercancel', this.onUp);
    }
}
